<template>
  <div id="map-displayer" ref="container"></div>
</template>

<script setup>
import L from 'leaflet'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { leafletIconForPin } from '../utils/pinIcons'
import { loadGoogleMapsApi } from '../utils/googleMaps'
import { maptilerAttribution, maptilerTileUrl } from '../utils/maptiler'

const props = defineProps({
  pins: {
    type: Array,
    required: true,
  },
  editMode: {
    type: Boolean,
    required: true,
  },
  editPin: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['pin-selected'])

const container = ref(null)
let map = null
let geocoderPromise = null
let markers = []

function clearMarkers() {
  markers.forEach((marker) => marker.remove())
  markers = []
}

function markerIcon(iconOptions) {
  return L.icon(iconOptions)
}

function renderMarkers() {
  if (!map) return

  clearMarkers()

  if (props.editMode && props.editPin.position) {
    markers.push(
      L.marker([props.editPin.position.lat, props.editPin.position.lng], {
        icon: markerIcon(leafletIconForPin({})),
      }).addTo(map),
    )
    return
  }

  props.pins.forEach((pin) => {
    const marker = L.marker([pin.position.lat, pin.position.lng], {
      icon: markerIcon(leafletIconForPin(pin)),
      title: pin.name,
    }).addTo(map)

    marker.on('click', () => emit('pin-selected', pin))
    markers.push(marker)
  })
}

function resizeMap() {
  if (map) map.invalidateSize()
}

function centerPin(pin) {
  if (!pin) return
  setTimeout(() => {
    if (map) map.setView([pin.position.lat, pin.position.lng], map.getZoom(), { animate: false })
  }, 320)
}

function getGeocoder() {
  if (!geocoderPromise) {
    geocoderPromise = loadGoogleMapsApi().then(() => new google.maps.Geocoder())
  }

  return geocoderPromise
}

function updateAddressFromPosition() {
  getGeocoder().then((geocoder) => {
    geocoder.geocode({ location: props.editPin.position }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK || !results[0]) {
        window.alert(`Geocoder failed due to: ${status}`)
        return
      }

      const components = results[0].address_components
      props.editPin.city = components.find((item) => item.types.indexOf('locality') > -1)?.long_name || ''
      props.editPin.region = components.find((item) => item.types.indexOf('administrative_area_level_1') > -1)?.long_name || ''
      props.editPin.country = components.find((item) => item.types.indexOf('country') > -1)?.long_name || ''
    })
  })
}

onMounted(() => {
  if (!container.value) return

  container.value.style.height = `${window.innerHeight - 68}px`

  map = L.map(container.value, {
    center: [46.978596, 2.538574],
    zoom: 6,
  })

  L.tileLayer(maptilerTileUrl('base-v4'), {
    attribution: maptilerAttribution,
    crossOrigin: true,
    minZoom: 1,
    maxZoom: 20,
    tileSize: 512,
    zoomOffset: -1,
  }).addTo(map)

  map.on('click', (event) => {
    if (!props.editMode) return

    props.editPin.position.lat = event.latlng.lat
    props.editPin.position.lng = event.latlng.lng
    updateAddressFromPosition()
    renderMarkers()
  })

  window.addEventListener('resize', handleWindowResize)
  renderMarkers()
})

function handleWindowResize() {
  if (container.value) container.value.style.height = `${window.innerHeight - 68}px`
  resizeMap()
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleWindowResize)
  clearMarkers()
  if (map) map.remove()
})

watch(
  () => [props.pins, props.editMode, props.editPin.position.lat, props.editPin.position.lng],
  () => nextTick(renderMarkers),
  { deep: true },
)

defineExpose({
  centerPin,
  resizeMap,
})
</script>
